import { parseWorkflowStatusMessage, normalizeExecutionStatusResponse } from '../utils/workflowStatus';
import {
  STATUS_POLL_TIMEOUT_MS,
  SUBTRACT_MATCHES_JOB_CREATE_TIMEOUT_MS,
  SUBTRACT_MATCHES_JOB_RESULTS_TIMEOUT_MS,
} from '../utils/constants';
import {
  appendCitationParserFields,
  normalizeOcrText,
  normalizeUnmatchedGroups,
  parseUnmatchedCount,
} from '../components/citationParser/citationParserUtils';

export { parseWorkflowStatusMessage, normalizeExecutionStatusResponse };

function normalizeApiBaseUrl(input) {
  const raw = input != null ? String(input).trim() : '';
  if (!raw) return '';
  // Some deployments set REACT_APP_API_URL to ".../api" but this client already prefixes "/api/...".
  // Normalize to the origin/root to avoid ".../api/api/..." double-prefix bugs.
  const withoutTrailingSlashes = raw.replace(/\/+$/, '');
  const withoutApiSuffix = withoutTrailingSlashes.replace(/\/api$/i, '');
  return withoutApiSuffix;
}

// Use environment variable or default to relative URL.
// In production with nginx proxy, use relative URLs.
// The proxy will be set at build time or use the current origin.
export const API_BASE_URL = normalizeApiBaseUrl(process.env.REACT_APP_API_URL) || (
  process.env.NODE_ENV === 'production'
    ? '' // Use relative URLs in production (nginx will proxy)
    : `${window.location.protocol}//${window.location.hostname}:8000` // Use server IP/hostname in development
);

export function buildApiUrl(pathname) {
  const base = API_BASE_URL || '';
  const path = pathname || '';
  if (!base) return path.startsWith('/') ? path : `/${path}`;
  if (!path) return base;
  return `${base.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
}

/**
 * Builds query string from key-value params (skips null/undefined).
 * @param {Record<string, string|number|boolean|null|undefined>} params
 * @returns {string} e.g. "llm_system_id=1&status=completed"
 */
function buildQueryParams(params) {
  const pairs = Object.entries(params)
    .filter(([, v]) => v != null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return pairs.length ? '?' + pairs.join('&') : '';
}

function wrapSubtractMatchesStepError(step, err) {
  const message = err?.message || String(err);
  return new Error(`Failed while ${step}: ${message}`);
}

/**
 * Append JWT for WebSocket/SSE (EventSource cannot send Authorization header).
 * @param {string} url
 * @param {string|null|undefined} token
 * @returns {string}
 */
export function appendWorkflowAuthQuery(url, token) {
  if (!token) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}token=${encodeURIComponent(token)}`;
}

/**
 * @typedef {Object} LiteratureRef
 * @property {number} id
 * @property {string} title
 * @property {string|null} authors
 * @property {number|null} year
 * @property {string|null} doi
 * @property {string|null} journal
 * @property {Record<string, unknown>|null} [authoritative] - title, authors, year, doi, journal (no embedded raw API document)
 * @property {Record<string, unknown>|null} [discrepancies]
 */

/**
 * @typedef {Object} LLMSystemRef
 * @property {string|null} name
 * @property {string|null} function
 * @property {string|null} model_version
 * @property {string|null} subscription_status
 */

/**
 * @typedef {Object} GtFoundByLlmEntry
 * @property {LiteratureRef} reference
 * @property {LLMSystemRef[]} found_by_systems
 */

/**
 * @typedef {Object} AuthorReportResponse
 * @property {number} seed_paper_id
 * @property {LiteratureRef[]} deduplicated_llm_refs
 * @property {LiteratureRef[]} gt_not_in_llm
 * @property {LiteratureRef[]} llm_not_in_gt
 * @property {GtFoundByLlmEntry[]} [gt_found_by_llm]
 */

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    /** @type {null | (() => Promise<string | null | undefined>)} */
    this.accessTokenGetter = null;
  }

  /**
   * Provide a function that returns an access token.
   * @param {() => Promise<string | null | undefined>} getter
   */
  setAccessTokenGetter(getter) {
    this.accessTokenGetter = getter;
  }

  /** @returns {Promise<string|null>} */
  async getAccessToken() {
    if (!this.accessTokenGetter) return null;
    try {
      return (await this.accessTokenGetter()) || null;
    } catch {
      return null;
    }
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const isFormData = options.body instanceof FormData;
    // Pull headers out so spreading ...restOptions does not overwrite merged headers
    // (FormData calls use headers: {} only to avoid setting Content-Type; that must not drop Bearer auth).
    const { headers: optionHeaders = {}, json, timeout: timeoutOption, ...restOptions } = options;
    let token = null;
    if (this.accessTokenGetter) {
      try {
        token = await this.accessTokenGetter();
      } catch (e) {
        token = null;
      }
    }
    const useJsonContentType = json !== false && !isFormData;
    const config = {
      ...restOptions,
      headers: {
        ...(useJsonContentType ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...optionHeaders,
      },
    };

    // Default 5 minutes. timeout 0 or null means wait until the server responds.
    const timeout = timeoutOption === undefined ? 300000 : timeoutOption;
    const controller = new AbortController();
    const timeoutId = timeout
      ? setTimeout(() => controller.abort(), timeout)
      : null;
    const externalSignal = options.signal;
    if (externalSignal) {
      if (externalSignal.aborted) controller.abort();
      else externalSignal.addEventListener('abort', () => controller.abort(), { once: true });
    }

    try {
      const response = await fetch(url, { ...config, signal: controller.signal });
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          // Handle FastAPI validation errors
          if (errorData.detail) {
            if (Array.isArray(errorData.detail)) {
              // FastAPI validation errors are arrays
              errorMessage = errorData.detail.map(err => `${err.loc.join('.')}: ${err.msg}`).join(', ');
            } else if (typeof errorData.detail === 'string') {
              errorMessage = errorData.detail;
            } else {
              errorMessage = JSON.stringify(errorData.detail);
            }
          }
        } catch (e) {
          // If JSON parsing fails, use status text
          errorMessage = `HTTP error! status: ${response.status}`;
        }
        const err = new Error(errorMessage);
        err.status = response.status;
        throw err;
      }

      if (options.responseType === 'blob') {
        const blob = await response.blob();
        if (options.includeResponseHeaders) {
          return { data: blob, headers: response.headers };
        }
        return blob;
      }

      // Handle empty responses
      const contentType = response.headers.get('content-type');
      let data;
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }
      if (options.includeResponseHeaders) {
        return { data, headers: response.headers };
      }
      return data;
    } catch (error) {
      clearTimeout(timeoutId);
      
      // Handle abort/timeout
      if (error.name === 'AbortError') {
        if (!timeout) {
          throw error;
        }
        console.error('API request timed out:', endpoint);
        throw new Error(`Request timed out after ${timeout / 1000} seconds`);
      }
      
      // Handle 504 Gateway Timeout specifically
      if (error.message && error.message.includes('504')) {
        console.error('Gateway timeout - workflow may still be processing');
        throw new Error('Gateway timeout: The workflow is taking longer than expected. It may still be processing in the background.');
      }
      // Skip logging for expected "missing data" 400 — import flow handles it and shows the form
      const isPromptNotFound = error.message && /Prompt with id \d+ not found/i.test(error.message);
      const isSeedPaperNotFound = error.message && (/Seed paper (?:with id )?\d* ?not found/i.test(error.message) || /Seed paper .* not found/i.test(error.message));
      if (!isPromptNotFound && !isSeedPaperNotFound && !options.silentErrors) {
        console.error('API request failed:', error);
      }
      throw error;
    }
  }

  // Health check
  async healthCheck() {
    return this.request('/api/health');
  }

  // Current user (Auth)
  async getMe(options = {}) {
    return this.request('/api/user/me', options);
  }

  // Seed Papers
  async getSeedPapers(options = {}) {
    return this.request('/api/seed-papers', options);
  }

  /**
   * Add a new seed paper from either a BibTeX file or a citation string.
   * OpenAPI: POST /api/seed-papers (multipart/form-data) with one of: file|citation; optional alias.
   *
   * Backward-compatible usage:
   * - addSeedPaper(file, alias?)
   * - addSeedPaper(citationString, alias?)
   * - addSeedPaper({ file?: File, citation?: string, alias?: string })
   */
  async addSeedPaper(fileOrCitationOrPayload, alias = null) {
    const formData = new FormData();
    let file = null;
    let citation = null;
    let finalAlias = alias;

    if (fileOrCitationOrPayload && typeof fileOrCitationOrPayload === 'object' && !(fileOrCitationOrPayload instanceof File)) {
      file = fileOrCitationOrPayload.file ?? null;
      citation = fileOrCitationOrPayload.citation ?? null;
      finalAlias = fileOrCitationOrPayload.alias ?? finalAlias;
    } else if (fileOrCitationOrPayload instanceof File) {
      file = fileOrCitationOrPayload;
    } else if (typeof fileOrCitationOrPayload === 'string') {
      citation = fileOrCitationOrPayload;
    }

    const hasFile = file != null;
    const hasCitation = citation != null && String(citation).trim() !== '';
    if (!hasFile && !hasCitation) {
      throw new Error('Provide either a BibTeX file (.bib) or a citation string. One of them is required.');
    }
    if (hasFile && hasCitation) {
      throw new Error('Provide either a BibTeX file (.bib) or a citation string, not both.');
    }

    if (hasFile) formData.append('file', file);
    if (hasCitation) formData.append('citation', String(citation).trim());
    if (finalAlias != null && String(finalAlias).trim() !== '') {
      formData.append('alias', String(finalAlias).trim());
    }

    return this.request('/api/seed-papers', {
      method: 'POST',
      headers: {}, // Let browser set Content-Type for FormData
      body: formData,
    });
  }

  // Ground Truth References
  async getGroundTruthReferences(seedPaperId) {
    return this.request(`/api/seed-papers/${seedPaperId}/ground-truth`);
  }

  /**
   * Get author report for a seed paper (deduplicated LLM refs, GT not in LLM, LLM not in GT, GT found by LLM).
   * @param {number} seedPaperId
   * @param {object} [options]
   * @param {boolean} [options.enrich_llm_not_in_gt] - default true (backend)
   * @param {boolean} [options.force_refresh] - default false (backend)
   * @param {number|null} [options.max_enriched_items] - optional cap (backend)
   * @returns {Promise<AuthorReportResponse>}
   */
  async getAuthorReport(seedPaperId, options = {}) {
    const query = buildQueryParams({
      enrich_llm_not_in_gt: options?.enrich_llm_not_in_gt,
      force_refresh: options?.force_refresh,
      max_enriched_items: options?.max_enriched_items,
    });
    return this.request(`/api/seed-papers/${seedPaperId}/author-report${query}`);
  }

  /**
   * Cumulative + per-execution existence/GT counts for a seed paper.
   * No citation lists and no api_response. Join `executions[].id` to GET /api/executions.
   * @param {number} seedPaperId
   * @param {object} [options]
   * @param {string} [options.status] e.g. "completed"
   */
  async getSeedPaperExecutionSummaries(seedPaperId, options = {}) {
    const query = buildQueryParams({
      status: options?.status,
    });
    return this.request(`/api/seed-papers/${seedPaperId}/execution-summaries${query}`);
  }

  /**
   * Compute/cache authoritative citation metadata + discrepancy checks for all deduplicated
   * LLM references associated with a seed paper.
   *
   * OpenAPI: POST /api/seed-papers/{seed_paper_id}/authoritative-metadata/enrich
   * Query: force_refresh (bool, default false), authors_threshold (number, default 1.0)
   */
  async enrichAuthoritativeMetadataForSeedPaper(seedPaperId, options = {}) {
    const query = buildQueryParams({
      force_refresh: options?.force_refresh,
      authors_threshold: options?.authors_threshold,
    });
    return this.request(
      `/api/seed-papers/${seedPaperId}/authoritative-metadata/enrich${query}`,
      { method: 'POST' },
    );
  }

  async addGroundTruthReferences(seedPaperId, file) {
    const formData = new FormData();
    formData.append('file', file);
    
    return this.request(`/api/seed-papers/${seedPaperId}/ground-truth`, {
      method: 'POST',
      headers: {}, // Let browser set Content-Type for FormData
      body: formData,
    });
  }

  async deleteGroundTruthReference(referenceId) {
    return this.request(`/api/ground-truth/${referenceId}`, {
      method: 'DELETE',
    });
  }

  // Excluded Study References
  async getExcludedStudyReferences(seedPaperId) {
    return this.request(`/api/seed-papers/${seedPaperId}/excluded-studies`);
  }

  async addExcludedStudyReferences(seedPaperId, file) {
    const formData = new FormData();
    formData.append('file', file);

    return this.request(`/api/seed-papers/${seedPaperId}/excluded-studies`, {
      method: 'POST',
      headers: {}, // Let browser set Content-Type for FormData
      body: formData,
    });
  }

  async deleteExcludedStudyReference(referenceId) {
    return this.request(`/api/excluded-studies/${referenceId}`, {
      method: 'DELETE',
    });
  }

  /**
   * OCR a reference-section screenshot into an editable draft. Does not write the database.
   * POST /api/seed-papers/{id}/bibliography/preview — multipart field `files`.
   * Optional: parser=example + examples (JSON string). The list can be longer than two
   * when examples use different field orders. Omit parser for built-in (default).
   * Response stays JSON. citations is unchanged and may include unmatched_groups.
   * The example parser still sends X-Citation-Unmatched, returned here as unmatchedCount.
   * Requires seed_papers and citation_ocr.
   * @param {number|string} seedPaperId
   * @param {File[]} files
   * @param {{ parserMode?: string, examples?: Array, extraExamples?: Array }} [parserOptions]
   */
  async previewSeedBibliography(seedPaperId, files, parserOptions = {}) {
    const list = Array.isArray(files) ? files : [];
    const formData = new FormData();
    for (const file of list) {
      formData.append('files', file);
    }
    appendCitationParserFields(formData, parserOptions);
    const { data, headers } = await this.request(
      `/api/seed-papers/${seedPaperId}/bibliography/preview`,
      {
        method: 'POST',
        headers: {},
        body: formData,
        includeResponseHeaders: true,
        timeout: 0,
      },
    );
    const unmatchedCount = parseUnmatchedCount(headers?.get?.('X-Citation-Unmatched'));
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      return { ...data, unmatchedCount };
    }
    return data;
  }

  /**
   * Replace the stored bibliography with an approved citation list.
   * POST /api/seed-papers/{id}/bibliography — JSON `{ citations }`. An empty array clears it.
   * @param {number|string} seedPaperId
   * @param {object[]} citations
   */
  async storeSeedBibliography(seedPaperId, citations) {
    const list = Array.isArray(citations) ? citations : [];
    return this.request(`/api/seed-papers/${seedPaperId}/bibliography`, {
      method: 'POST',
      body: JSON.stringify({ citations: list }),
    });
  }

  /**
   * Replace the stored bibliography from a BibTeX, JSON, RIS, or CSV file.
   * POST /api/seed-papers/{id}/bibliography — multipart field `file`.
   * @param {number|string} seedPaperId
   * @param {File} file
   */
  async storeSeedBibliographyFile(seedPaperId, file) {
    const formData = new FormData();
    formData.append('file', file);
    return this.request(`/api/seed-papers/${seedPaperId}/bibliography`, {
      method: 'POST',
      headers: {},
      body: formData,
    });
  }

  /** GET /api/seed-papers/{id}/bibliography — bare array of stored references. */
  async getSeedBibliography(seedPaperId) {
    return this.request(`/api/seed-papers/${seedPaperId}/bibliography`);
  }

  /**
   * DELETE /api/bibliography/{referenceId}.
   * referenceId is the bibliography link id (references[].id), not a literature id.
   */
  async deleteBibliographyReference(referenceId) {
    return this.request(`/api/bibliography/${referenceId}`, {
      method: 'DELETE',
    });
  }

  /** GET /api/seed-papers/{id}/bibliography-comparison — stored disclosure result. */
  async getSeedBibliographyComparison(seedPaperId) {
    return this.request(`/api/seed-papers/${seedPaperId}/bibliography-comparison`);
  }

  /**
   * POST /api/seed-papers/{id}/bibliography-comparison — recompute disclosure. No body.
   * Call after ground truth is added or changed.
   */
  async recomputeSeedBibliographyComparison(seedPaperId) {
    return this.request(`/api/seed-papers/${seedPaperId}/bibliography-comparison`, {
      method: 'POST',
    });
  }

  // Prompts
  async getPrompts(options = {}) {
    return this.request('/api/prompts', options);
  }

  /**
   * Add a new prompt from either inline text (prompt_content) or an uploaded text file (one required).
   * @param { { prompt_content?: string, content?: string, file?: File } } payload - Inline prompt text and/or uploaded file; at least one required
   * @param { number | null } seedPaperId
   * @param { string | null } version
   * @param { string | null } alias
   */
  async addPrompt(payload, seedPaperId = null, version = null, alias = null) {
    const { prompt_content, content, file } = payload || {};
    const text = prompt_content ?? content;
    const hasText = text != null && String(text).trim() !== '';
    const hasFile = file != null;
    if (!hasText && !hasFile) {
      throw new Error('Provide either prompt text (prompt_content) or upload a text file (.txt). One of them is required.');
    }
    const formData = new FormData();
    if (hasText) {
      formData.append('prompt_content', String(text).trim());
    }
    if (hasFile) {
      formData.append('file', file);
    }
    if (seedPaperId != null && String(seedPaperId).trim() !== '') {
      formData.append('seed_paper_id', seedPaperId);
    }
    if (version != null && String(version).trim() !== '') {
      formData.append('version', String(version).trim());
    }
    if (alias != null && String(alias).trim() !== '') {
      formData.append('alias', String(alias).trim());
    }

    return this.request('/api/prompts', {
      method: 'POST',
      headers: {}, // Let browser set Content-Type for FormData
      body: formData,
    });
  }

  // LLM Models
  async getLLMModels() {
    return this.request('/api/llm-models');
  }

  // Workflow
  async executeWorkflow(workflowRequest) {
    return this.request('/api/workflow/execute', {
      method: 'POST',
      body: JSON.stringify(workflowRequest),
      timeout: 600000, // 10 minutes timeout for workflow execution
    });
  }

  async getExecutionStatus(executionId) {
    const raw = await this.request(`/api/workflow/${executionId}/status`, {
      timeout: STATUS_POLL_TIMEOUT_MS,
    });
    return normalizeExecutionStatusResponse(raw) ?? raw;
  }

  /**
   * Import / post-import verification status (flat ExecutionStatusResponse).
   * OpenAPI: GET /api/executions/{execution_id}/status
   * Use after an import job exposes a numeric execution_id (insert succeeded).
   * Never pass live-store keys like `import-{run_id}` here.
   */
  async getImportExecutionStatus(executionId) {
    const id = executionId != null ? String(executionId).trim() : '';
    if (!/^\d+$/.test(id)) {
      throw new Error(
        `Invalid execution_id for /api/executions/{id}/status (got "${executionId}"). Use import job status until a numeric execution_id exists.`
      );
    }
    const raw = await this.request(`/api/executions/${id}/status`, {
      timeout: STATUS_POLL_TIMEOUT_MS,
    });
    return normalizeExecutionStatusResponse(raw) ?? raw;
  }

  /**
   * Poll async import job status (accept-and-queue import).
   * OpenAPI: GET /api/executions/import/jobs/{run_id}/status
   * Prefer calling with the 202 `status_url` when available (via getByApiPath).
   * status: pending | running | completed | failed
   * execution_id present after insert succeeds; error_message on insert failure.
   */
  async getImportJobStatus(runId) {
    if (runId == null) throw new Error('run_id is required.');
    const raw = String(runId).trim();
    const storeKey = /^import-(\d+)$/i.exec(raw);
    const id = storeKey ? storeKey[1] : raw;
    if (!id) throw new Error('run_id is required.');
    return this.request(`/api/executions/import/jobs/${encodeURIComponent(id)}/status`, {
      timeout: STATUS_POLL_TIMEOUT_MS,
    });
  }

  /**
   * GET a relative API path from a 202 `status_url` (e.g. /api/executions/import/jobs/64/status).
   * @param {string} apiPath
   */
  async getByApiPath(apiPath) {
    const path = apiPath != null ? String(apiPath).trim() : '';
    if (!path) throw new Error('api path is required.');
    if (/^https?:\/\//i.test(path)) {
      // Absolute URLs: strip origin so request() can prefix baseURL consistently when needed.
      try {
        const u = new URL(path);
        return this.request(`${u.pathname}${u.search}`, { timeout: STATUS_POLL_TIMEOUT_MS });
      } catch {
        throw new Error(`Invalid api path: ${apiPath}`);
      }
    }
    const endpoint = path.startsWith('/') ? path : `/${path}`;
    return this.request(endpoint, { timeout: STATUS_POLL_TIMEOUT_MS });
  }

  async getExecutionResults(executionId) {
    // Results may require additional time to be assembled after completion.
    return this.request(`/api/workflow/${executionId}/results`, { timeout: 600000 });
  }

  async exportResults(executionId, format = 'json') {
    return this.request(`/api/workflow/${executionId}/export?format=${format}`);
  }

  // Publication Verifier
  /**
   * Publication verifier (OpenAPI: Body_verify_publications_api_publication_verifier_verify_post).
   * Accepts either a ready FormData or a plain object payload:
   * { file, email?, api_key?, enrich_doi?, authoritative_verification_mode? }.
   * @param {FormData | {file: File, email?: string, api_key?: string, enrich_doi?: boolean, authoritative_verification_mode?: string}} payload
   */
  async verifyPublications(payload) {
    const formData = payload instanceof FormData ? payload : this._buildPublicationVerifierFormData(payload);
    return this.request('/api/publication-verifier/verify', {
      method: 'POST',
      headers: {}, // Let browser set Content-Type for FormData
      body: formData,
    });
  }

  /**
   * Publication verifier (with storage) (OpenAPI: Body_verify_publications_with_storage_api_publication_verifier_verify_with_storage_post).
   * Accepts either a ready FormData or a plain object payload:
   * { file, execution_name, email?, api_key?, enrich_doi?, authoritative_verification_mode? }.
   * @param {FormData | {file: File, execution_name: string, email?: string, api_key?: string, enrich_doi?: boolean, authoritative_verification_mode?: string}} payload
   */
  async verifyPublicationsWithStorage(payload) {
    const formData = payload instanceof FormData ? payload : this._buildPublicationVerifierFormData(payload, { requireExecutionName: true });
    return this.request('/api/publication-verifier/verify-with-storage', {
      method: 'POST',
      headers: {}, // Let browser set Content-Type for FormData
      body: formData,
    });
  }

  /**
   * Build multipart/form-data for publication verifier endpoints.
   * @param {object} payload
   * @param {object} [options]
   * @param {boolean} [options.requireExecutionName]
   * @returns {FormData}
   */
  _buildPublicationVerifierFormData(payload = {}, options = {}) {
    const requireExecutionName = !!options.requireExecutionName;
    const {
      file,
      email,
      api_key,
      enrich_doi,
      execution_name,
      comparison_profile_id,
      existence_check_mode,
      authoritative_verification_mode,
    } = payload || {};

    if (!(file instanceof File)) {
      throw new Error('Publication verifier: `file` is required and must be a File.');
    }
    if (requireExecutionName && (!execution_name || String(execution_name).trim() === '')) {
      throw new Error('Publication verifier (with storage): `execution_name` is required.');
    }

    const formData = new FormData();
    formData.append('file', file);

    if (email != null && String(email).trim() !== '') {
      formData.append('email', String(email).trim());
    }
    if (api_key != null && String(api_key).trim() !== '') {
      formData.append('api_key', String(api_key).trim());
    }
    if (enrich_doi !== undefined && enrich_doi !== null) {
      // FastAPI Form(bool) works reliably with "true"/"false" strings
      formData.append('enrich_doi', enrich_doi ? 'true' : 'false');
    }
    if (authoritative_verification_mode != null && String(authoritative_verification_mode).trim() !== '') {
      formData.append('authoritative_verification_mode', String(authoritative_verification_mode).trim());
    }
    if (comparison_profile_id != null && String(comparison_profile_id).trim() !== '') {
      formData.append('comparison_profile_id', String(comparison_profile_id).trim());
    }
    if (existence_check_mode != null && String(existence_check_mode).trim() !== '') {
      formData.append('existence_check_mode', String(existence_check_mode).trim());
    }
    if (requireExecutionName) {
      formData.append('execution_name', String(execution_name).trim());
    }

    return formData;
  }

  async exportVerificationResults(results, format = 'text') {
    return this.request(`/api/publication-verifier/export?format=${format}`, {
      method: 'POST',
      body: JSON.stringify(results),
      responseType: 'blob',
    });
  }

  /**
   * Citation multi-search (OpenAPI: CitationMultiSearchRequest).
   * POST /api/publication-verifier/citation-multi-search (application/json)
   * Any subset of: email, api_key, citation_bibtex, title, authors (array|string), author,
   * year (number|string), journal, booktitle, venue, doi.
   */
  async citationMultiSearch(payload) {
    return this.request('/api/publication-verifier/citation-multi-search', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async getPublicationVerifierHealth() {
    return this.request('/api/publication-verifier/health');
  }

  // Reference Comparer
  async comparePublications(formData) {
    return this.request('/api/reference-comparer/compare', {
      method: 'POST',
      headers: {}, // Let browser set Content-Type for FormData
      body: formData,
    });
  }

  async comparePublicationsWithStorage(formData) {
    return this.request('/api/reference-comparer/compare-with-storage', {
      method: 'POST',
      headers: {}, // Let browser set Content-Type for FormData
      body: formData,
    });
  }

  /**
   * Build multipart body for subtract-matches (sync or async job create).
   * @param {{
   *   sourceFiles: File[],
   *   targetFile: File,
   *   comparisonProfileId?: number|null,
   *   removePartialMatches?: boolean,
   *   dedupeMode?: 'profile_full_partial'|'title_only'|'both_always',
   * }} payload
   */
  _buildSubtractMatchesFormData({
    sourceFiles,
    targetFile,
    comparisonProfileId = null,
    removePartialMatches = false,
    dedupeMode = 'profile_full_partial',
  }) {
    const list = Array.isArray(sourceFiles) ? sourceFiles.filter((file) => file instanceof File) : [];
    if (list.length === 0 || !(targetFile instanceof File)) {
      throw new Error('At least one source file and one target file are required.');
    }
    const formData = new FormData();
    for (const file of list) {
      formData.append('source_files', file);
    }
    formData.append('target_file', targetFile);
    if (comparisonProfileId != null) {
      formData.append('comparison_profile_id', String(comparisonProfileId));
    }
    formData.append('remove_partial_matches', String(Boolean(removePartialMatches)));
    formData.append('dedupe_mode', String(dedupeMode || 'profile_full_partial'));
    return formData;
  }

  /**
   * Synchronous subtract-matches.
   * OpenAPI: POST /api/reference-comparer/subtract-matches
   */
  async subtractMatches(payload) {
    const formData = this._buildSubtractMatchesFormData(payload);
    try {
      return await this.request('/api/reference-comparer/subtract-matches', {
        method: 'POST',
        headers: {},
        body: formData,
        timeout: SUBTRACT_MATCHES_JOB_CREATE_TIMEOUT_MS,
      });
    } catch (err) {
      throw wrapSubtractMatchesStepError('synchronous subtract-matches', err);
    }
  }

  /**
   * Create a persisted async subtract-matches job.
   * OpenAPI: POST /api/reference-comparer/subtract-matches/jobs
   */
  async createSubtractMatchesJob(payload) {
    const formData = this._buildSubtractMatchesFormData(payload);
    try {
      return await this.request('/api/reference-comparer/subtract-matches/jobs', {
        method: 'POST',
        headers: {},
        body: formData,
        timeout: SUBTRACT_MATCHES_JOB_CREATE_TIMEOUT_MS,
      });
    } catch (err) {
      throw wrapSubtractMatchesStepError('creating subtract-matches job (upload/create)', err);
    }
  }

  /**
   * OpenAPI: GET /api/reference-comparer/subtract-matches/jobs/{run_id}/status
   */
  async getSubtractMatchesJobStatus(runId) {
    if (runId == null) throw new Error('run_id is required.');
    try {
      return await this.request(
        `/api/reference-comparer/subtract-matches/jobs/${encodeURIComponent(runId)}/status`,
        { timeout: STATUS_POLL_TIMEOUT_MS }
      );
    } catch (err) {
      throw wrapSubtractMatchesStepError(`polling subtract-matches job #${runId} status`, err);
    }
  }

  /**
   * OpenAPI: GET /api/reference-comparer/subtract-matches/jobs/{run_id}/results
   */
  async getSubtractMatchesJobResults(runId) {
    if (runId == null) throw new Error('run_id is required.');
    try {
      return await this.request(
        `/api/reference-comparer/subtract-matches/jobs/${encodeURIComponent(runId)}/results`,
        { timeout: SUBTRACT_MATCHES_JOB_RESULTS_TIMEOUT_MS }
      );
    } catch (err) {
      throw wrapSubtractMatchesStepError(`fetching subtract-matches job #${runId} results`, err);
    }
  }

  /**
   * OpenAPI: GET /api/reference-comparer/subtract-matches/jobs
   * @param {{ limit?: number, offset?: number }} params
   */
  async listSubtractMatchesJobs({ limit = 50, offset = 0 } = {}) {
    const query = buildQueryParams({ limit, offset });
    return this.request(`/api/reference-comparer/subtract-matches/jobs${query}`);
  }

  /**
   * Batch GT comparison: LLM files vs uploaded BibTeX or seed paper GT from DB.
   * Provide exactly one of groundTruthFile or seedPaperId.
   * OpenAPI: POST /api/reference-comparer/compare-batch
   * @param {{
   *   groundTruthFile?: File|null,
   *   llmFiles: File[],
   *   comparisonProfileId?: number|null,
   *   persistResults?: boolean,
   *   includePartial?: boolean,
   *   seedPaperId?: number|null,
   * }} payload
   */
  async compareBatchPublications({
    groundTruthFile = null,
    llmFiles,
    comparisonProfileId,
    persistResults = false,
    includePartial = true,
    seedPaperId = null,
  }) {
    const list = Array.isArray(llmFiles) ? llmFiles : [];
    const resolvedSeedPaperId =
      seedPaperId != null && seedPaperId !== '' ? Number(seedPaperId) : null;
    const useSeedPaper =
      Number.isInteger(resolvedSeedPaperId) && resolvedSeedPaperId > 0;
    const useUploadedFile = !useSeedPaper && groundTruthFile instanceof File;

    if (!useSeedPaper && !useUploadedFile) {
      throw new Error('Ground truth file or seed paper is required.');
    }
    if (list.length === 0) {
      throw new Error('At least one LLM file is required.');
    }

    const formData = new FormData();
    if (useSeedPaper) {
      formData.append('seed_paper_id', String(resolvedSeedPaperId));
    } else {
      formData.append('ground_truth_file', groundTruthFile);
    }
    for (const f of list) {
      formData.append('llm_files', f);
    }
    if (comparisonProfileId != null) {
      formData.append('comparison_profile_id', String(comparisonProfileId));
    }
    formData.append('persist_results', String(Boolean(persistResults)));
    formData.append('include_partial', String(includePartial !== false));
    return this.request('/api/reference-comparer/compare-batch', {
      method: 'POST',
      headers: {},
      body: formData,
    });
  }

  /**
   * Diagnostic: parse uploaded citation files and report metadata-field completeness.
   * No ground truth, matching, or persistence.
   * OpenAPI: POST /api/reference-comparer/citation-metadata-completeness
   * Multipart field: llm_files (repeat per file; `files` is an accepted alias on the API).
   * @param {File[]} llmFiles
   */
  async getCitationMetadataCompleteness(llmFiles) {
    const list = Array.isArray(llmFiles) ? llmFiles.filter((f) => f instanceof File) : [];
    if (list.length === 0) {
      throw new Error('At least one LLM output file is required');
    }
    const formData = new FormData();
    for (const f of list) {
      formData.append('llm_files', f);
    }
    return this.request('/api/reference-comparer/citation-metadata-completeness', {
      method: 'POST',
      headers: {},
      body: formData,
    });
  }

  /**
   * @typedef {Object} BatchComparisonMetricStats
   * @property {number|null} min
   * @property {number|null} max
   * @property {number|null} nz_avg - Mean of non-null values (zeros count; nulls excluded)
   * @property {number|null} nz_median - Median of non-null values
   * @property {number|null} std_dev
   * @property {number|null} iqr
   */

  /**
   * @typedef {Object} BatchComparisonGroupedStats
   * @property {number} count
   * @property {number|null} total_llm_papers_sum
   * @property {number} rej_rate - Fraction of rejected rows (0.0–1.0); rejection means total_llm_papers == 0
   * @property {number} rej_count - Count of rejected rows
   * @property {BatchComparisonMetricStats|null} precision
   * @property {BatchComparisonMetricStats|null} recall
   * @property {BatchComparisonMetricStats|null} f1_score
   * @property {BatchComparisonMetricStats|null} existence_precision - Share of LLM citations verified in academic databases
   */

  /**
   * @typedef {Object} BatchComparisonResultRow
   * @property {number} id
   * @property {number} run_id
   * @property {number} seed_paper_id
   * @property {string|null} prompt_alias
   * @property {number|null} comparison_profile_id
   * @property {string|null} system_key
   * @property {number|null} total_llm_papers
   * @property {number|null} precision
   * @property {number|null} recall
   * @property {number|null} f1_score
   * @property {number|null} existence_precision - 0..1, or null if not stored
   * @property {number|null} true_positives
   * @property {number|null} false_positives
   * @property {number|null} false_negatives
   * @property {string|null} created_at
   */

  /**
   * Distinct prompt_alias values stored for a seed paper in batch_comparison_results.
   * @param {number} seedPaperId
   * @returns {Promise<string[]|{ prompt_aliases: string[] }>}
   */
  async listBatchComparisonPromptAliases(seedPaperId) {
    if (seedPaperId == null) {
      throw new Error('Seed paper is required.');
    }
    const query = buildQueryParams({ seed_paper_id: seedPaperId });
    return this.request(`/api/reference-comparer/batch-results/prompt-aliases${query}`);
  }

  /**
   * List stored batch comparison results.
   * @param {{
   *   seedPaperId?: number|null,
   *   seedPaperIds?: number[]|null,
   *   promptAlias?: string|null,
   *   promptAliases?: string[]|null,
   *   comparisonProfileId?: number|null,
   *   runId?: number|null,
   *   latestOnly?: boolean,
   * }} filters
   */
  async listBatchComparisonResults({
    seedPaperId = null,
    seedPaperIds = null,
    promptAlias = null,
    promptAliases = null,
    comparisonProfileId = null,
    runId = null,
    latestOnly = true,
  } = {}) {
    const aliasesParam = promptAliases?.length
      ? promptAliases.join(',')
      : promptAlias != null && String(promptAlias).trim() !== ''
        ? String(promptAlias).trim()
        : null;
    const query = buildQueryParams({
      seed_paper_id: seedPaperId != null && !seedPaperIds?.length ? seedPaperId : null,
      seed_paper_ids: seedPaperIds?.length ? seedPaperIds.join(',') : null,
      prompt_alias: aliasesParam && !promptAliases?.length ? aliasesParam : null,
      prompt_aliases: promptAliases?.length ? aliasesParam : null,
      comparison_profile_id: comparisonProfileId,
      run_id: runId,
      latest_only: latestOnly,
    });
    return this.request(`/api/reference-comparer/batch-results${query}`);
  }

  /**
   * Compare stored batch results across seed papers and/or prompts.
   * All filters are applied server-side before aggregation.
   * @param {{
   *   seedPaperIds: number[],
   *   promptIds?: number[]|null,
   *   promptAliases?: string[]|null,
   *   comparisonProfileId?: number|null,
   *   systemKey?: string|null,
   *   systemKeys?: string[]|null,
   *   latestOnly?: boolean,
   * }} filters
   */
  async compareBatchComparisonResults({
    seedPaperIds,
    promptIds = null,
    promptAliases = null,
    comparisonProfileId = null,
    systemKey = null,
    systemKeys = null,
    latestOnly = false,
  }) {
    const ids = Array.isArray(seedPaperIds) ? seedPaperIds : [];
    if (ids.length === 0) {
      throw new Error('At least one seed paper is required.');
    }
    const aliasesParam = promptAliases?.length
      ? promptAliases.join(',')
      : null;
    const resolvedSystemKeys = systemKeys?.length
      ? [...new Set(systemKeys.map((key) => String(key).trim()).filter(Boolean))]
      : systemKey?.trim()
        ? [systemKey.trim()]
        : [];
    const query = buildQueryParams({
      seed_paper_ids: ids.join(','),
      prompt_ids: promptIds?.length ? promptIds.join(',') : null,
      prompt_aliases: aliasesParam,
      comparison_profile_id: comparisonProfileId,
      system_keys: resolvedSystemKeys.length ? resolvedSystemKeys.join(',') : null,
      latest_only: latestOnly,
    });
    return this.request(`/api/reference-comparer/batch-results/compare${query}`);
  }

  /**
   * Paginated history of batch comparison upload runs.
   * @param {{ limit?: number, offset?: number }} params
   */
  async listBatchComparisonRuns({ limit = 50, offset = 0 } = {}) {
    const query = buildQueryParams({ limit, offset });
    return this.request(`/api/reference-comparer/batch-results/runs${query}`);
  }

  async exportComparisonResults(results, format = 'json') {
    return this.request(`/api/reference-comparer/export?format=${format}`, {
      method: 'POST',
      body: JSON.stringify(results),
      responseType: 'blob',
    });
  }

  // Execution Management
  async getExecutions(llmSystemId = null, seedPaperId = null, promptId = null, status = null, limit = null) {
    const query = buildQueryParams({
      llm_system_id: llmSystemId,
      seed_paper_id: seedPaperId,
      prompt_id: promptId,
      status,
      limit,
    });
    return this.request(`/api/executions${query}`);
  }

  async getExecutionDetails(executionId) {
    return this.request(`/api/executions/${executionId}`);
  }

  /**
   * Slim by default (no api_response). Pass includeApiResponse for expand/raw DB hit.
   * @param {number} executionId
   * @param {object} [options]
   * @param {boolean} [options.includeApiResponse]
   */
  async getExecutionVerificationResults(executionId, options = {}) {
    const query = buildQueryParams({
      include_api_response: options?.includeApiResponse === true ? true : undefined,
    });
    return this.request(`/api/executions/${executionId}/verification-results${query}`);
  }

  async getExecutionComparisonResults(executionId) {
    return this.request(`/api/executions/${executionId}/comparison-results`);
  }

  /**
   * @param {FormData} formData
   * @param {Record<string, unknown>} options
   */
  _appendImportExecutionOptions(formData, options = {}) {
    if (options.seed_paper_id != null) {
      formData.append('seed_paper_id', String(options.seed_paper_id));
    }
    if (options.seed_paper_content != null && options.seed_paper_content.trim() !== '') {
      formData.append('seed_paper_content', options.seed_paper_content.trim());
    }
    if (options.seed_paper_alias != null && options.seed_paper_alias.trim() !== '') {
      formData.append('seed_paper_alias', options.seed_paper_alias.trim());
    }
    if (options.ground_truth_content != null && String(options.ground_truth_content).trim() !== '') {
      formData.append('ground_truth_content', String(options.ground_truth_content).trim());
    }
    if (options.ground_truth_content_type != null && String(options.ground_truth_content_type).trim() !== '') {
      formData.append('ground_truth_content_type', String(options.ground_truth_content_type).trim());
    }
    if (options.prompt_id != null) {
      formData.append('prompt_id', String(options.prompt_id));
    }
    if (options.prompt_content != null && options.prompt_content.trim() !== '') {
      formData.append('prompt_content', options.prompt_content.trim());
    }
    if (options.execution_comment != null && String(options.execution_comment).trim() !== '') {
      formData.append('execution_comment', String(options.execution_comment).trim());
    }
    if (options.existence_check_mode != null && String(options.existence_check_mode).trim() !== '') {
      formData.append('existence_check_mode', String(options.existence_check_mode).trim());
    }
    if (options.authoritative_verification_mode != null && String(options.authoritative_verification_mode).trim() !== '') {
      formData.append('authoritative_verification_mode', String(options.authoritative_verification_mode).trim());
    }
    if (options.verification_profile_id != null) {
      formData.append('verification_profile_id', String(options.verification_profile_id));
    }
    if (options.gt_comparison_profile_id != null) {
      formData.append('gt_comparison_profile_id', String(options.gt_comparison_profile_id));
    }
  }

  /**
   * Import execution from uploaded file (JSON, BibTeX, RIS, CSV, or .txt for _na no-result executions).
   * Filename must follow: name[.function]_modelversion_subscription_seedpaperID_promptID_promptversion_YYMMDD_HHMMSS[_comment].json|.bib|.ris|.csv|.txt
   * Example: chatgpt.consensus_gpt4_free_test1_prompt1_v3_250729_131049.json
   * Options: execution_comment (for _na .txt imports – file body stored as execution comment).
   *   verification_profile_id, gt_comparison_profile_id (omit for server defaults).
   * Accept-and-queue: HTTP 202. Does not return execution_id.
   * Single file: { status: 'accepted', run_id, filename, status_url, events_url }.
   * Track via GET /api/executions/import/jobs/{run_id}/status or /events until terminal;
   * execution_id appears on the job only after insert succeeds.
   */
  async importExecutionFromFile(file, options = {}) {
    const formData = new FormData();
    formData.append('file', file);
    this._appendImportExecutionOptions(formData, options);
    return this.request('/api/executions/import', {
      method: 'POST',
      headers: {}, // Let browser set Content-Type for FormData (multipart/form-data)
      body: formData,
    });
  }

  /**
   * Import one or more execution export files. Single file uses multipart field `file`; two or more use repeated `files` parts.
   * Accept-and-queue: HTTP 202. Single file → flat accepted payload; 2+ files → { total_files, runs: [...] }.
   * Each run has run_id (not execution_id). Poll/SSE import job endpoints per run_id.
   */
  async importExecutionFromFiles(files, options = {}) {
    const list = Array.isArray(files) ? files : [];
    if (list.length === 0) {
      throw new Error('No files selected');
    }
    const formData = new FormData();
    if (list.length === 1) {
      formData.append('file', list[0]);
    } else {
      for (const f of list) {
        formData.append('files', f);
      }
    }
    this._appendImportExecutionOptions(formData, options);
    return this.request('/api/executions/import', {
      method: 'POST',
      headers: {},
      body: formData,
    });
  }

  // Literature Management
  async getLiterature() {
    return this.request('/api/literature');
  }

  async getLiteratureVerificationResults(literatureId) {
    return this.request(`/api/literature/${literatureId}/verification-results`);
  }

  // Evaluation Metrics
  async evaluateExecution(verificationResults, comparisonResults, options = {}) {
    const requestBody = {
      verification_results: verificationResults,
      comparison_results: comparisonResults,
      include_partial: options.include_partial !== undefined ? options.include_partial : true,
    };
    
    // Add optional parameters if provided
    if (options.validity_weight !== undefined) {
      requestBody.validity_weight = options.validity_weight;
    }
    if (options.relevance_weight !== undefined) {
      requestBody.relevance_weight = options.relevance_weight;
    }
    if (options.save_to_file !== undefined) {
      requestBody.save_to_file = options.save_to_file;
    }
    
    return this.request('/api/evaluation/evaluate', {
      method: 'POST',
      body: JSON.stringify(requestBody),
    });
  }

  /**
   * Recalculate metrics for an existing execution and persist them server-side.
   * OpenAPI: POST /api/evaluation/executions/{execution_id}/recalculate
   * Body (ExecutionRecalculateMetricsRequest): include_partial, wmcc_weight (positive),
   * validity_weight, relevance_weight, openalex_email (optional, for verification pass).
   * @param {number} executionId
   * @param {object} [payload]
   * @param {boolean} [payload.include_partial]
   * @param {number} [payload.wmcc_weight]
   * @param {number} [payload.validity_weight]
   * @param {number} [payload.relevance_weight]
   * @param {string|null} [payload.openalex_email]
   * @returns {Promise<{ execution_id: number, execution_evaluation_id: number, evaluation: object }>}
   */
  async recalculateMetricsForExecution(executionId, payload = {}) {
    return this.request(`/api/evaluation/executions/${executionId}/recalculate`, {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    });
  }

  /**
   * List LLM citations that are not found (or never verified) for a seed paper.
   * OpenAPI: GET /api/evaluation/seed-papers/{seed_paper_id}/not-found-citations
   * @param {number} seedPaperId
   * @returns {Promise<{
   *   seed_paper_id: number,
   *   count: number,
   *   citations: Array<{
   *     execution_id: number,
   *     literature_id: number,
   *     title?: string,
   *     authors?: string,
   *     year?: number,
   *     doi?: string,
   *     databases_queried?: string[],
   *     database_results?: object,
   *     reasons?: string[],
   *     raw_response_status?: 'ok'|'missing'|'truncated', // informational only; does not block re-verify
   *     source_key?: string|null,
   *     llm_name?: string|null,
   *     prompt_alias?: string|null,
   *   }>,
   * }>}
   */
  async getSeedPaperNotFoundCitations(seedPaperId) {
    if (seedPaperId == null) throw new Error('seed_paper_id is required.');
    return this.request(
      `/api/evaluation/seed-papers/${encodeURIComponent(seedPaperId)}/not-found-citations`
    );
  }

  /**
   * Queue existence re-verify + metrics recalculate for not-found citations under a seed paper.
   * OpenAPI: POST /api/evaluation/seed-papers/{seed_paper_id}/executions/recalculate
   * Returns 202 with a job payload (not a synchronous recalculate result).
   * @param {number} seedPaperId
   * @param {object} [payload]
   * @param {boolean} [payload.include_partial]
   * @param {number} [payload.wmcc_weight]
   * @param {number} [payload.validity_weight]
   * @param {number} [payload.relevance_weight]
   * @param {string|null} [payload.openalex_email]
   * @param {number[]} [payload.literature_ids] - subset to re-verify; omit for all not-found
   * @returns {Promise<{
   *   job_id: number,
   *   run_id: number,
   *   status: string,
   *   status_url?: string,
   *   seed_paper_id: number,
   *   candidates?: object[],
   *   candidate_count?: number,
   *   skipped_raw_unavailable?: object[], // always [] (schema-compatible)
   *   skipped_raw_unavailable_count?: number, // always 0
   *   message?: string, // always "Existence re-verify job queued"
   * }>}
   */
  async recalculateMetricsForSeedPaperExecutions(seedPaperId, payload = {}) {
    return this.request(`/api/evaluation/seed-papers/${seedPaperId}/executions/recalculate`, {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    });
  }

  /**
   * Fire-and-forget existence re-verify for all not-found citations under a seed paper.
   * OpenAPI: POST /api/evaluation/seed-papers/{seed_paper_id}/reverify-not-found
   * No request body (no JSON, literature_ids, or metric weights). Returns 202.
   * @param {number} seedPaperId
   * @returns {Promise<{
   *   job_id: number,
   *   run_id: number,
   *   status: string,
   *   seed_paper_id: number,
   *   candidate_count: number,
   *   message: string,
   * }>}
   */
  async queueSeedPaperNotFoundReverify(seedPaperId) {
    if (seedPaperId == null) throw new Error('seed_paper_id is required.');
    return this.request(
      `/api/evaluation/seed-papers/${encodeURIComponent(seedPaperId)}/reverify-not-found`,
      { method: 'POST', json: false }
    );
  }

  /**
   * Poll evaluation background job status.
   * OpenAPI: GET /api/evaluation/jobs/{run_id}/status
   * On completed, may include result keys: reparsed, literature_updated,
   * skipped_reparse_match_failed (skipped_raw_unavailable no longer returned).
   * @param {number|string} runId
   * @returns {Promise<{
   *   run_id: number,
   *   status: string,
   *   progress?: number|null,
   *   message?: string|null,
   *   current_stage?: string|null,
   *   error?: string|null,
   *   job_type?: string|null,
   *   execution_id?: number|null,
   *   reparsed?: number,
   *   literature_updated?: number,
   *   skipped_raw_unavailable?: object[],
   *   skipped_reparse_match_failed?: object[],
   *   result?: object,
   * }>}
   */
  async getEvaluationJobStatus(runId) {
    if (runId == null) throw new Error('run_id is required.');
    return this.request(`/api/evaluation/jobs/${encodeURIComponent(runId)}/status`, {
      timeout: STATUS_POLL_TIMEOUT_MS,
    });
  }

  /**
   * Compare stored execution evaluations across seed papers.
   * Same response shape as compareBatchComparisonResults.
   * OpenAPI: GET /api/evaluation/executions/compare
   * @param {{
   *   seedPaperIds: number[],
   *   promptIds?: number[]|null,
   *   promptAliases?: string[]|null,
   *   comparisonProfileId?: number|null,
   *   systemKey?: string|null,
   *   systemKeys?: string[]|null,
   *   includePartial?: boolean,
   *   latestOnly?: boolean,
   * }} filters
   */
  async compareExecutionEvaluations({
    seedPaperIds,
    promptIds = null,
    promptAliases = null,
    comparisonProfileId = null,
    systemKey = null,
    systemKeys = null,
    includePartial = true,
    latestOnly = false,
  }) {
    const ids = Array.isArray(seedPaperIds) ? seedPaperIds : [];
    if (ids.length === 0) {
      throw new Error('At least one seed paper is required.');
    }
    const aliasesParam = promptAliases?.length
      ? promptAliases.join(',')
      : null;
    const resolvedSystemKeys = systemKeys?.length
      ? [...new Set(systemKeys.map((key) => String(key).trim()).filter(Boolean))]
      : systemKey?.trim()
        ? [systemKey.trim()]
        : [];
    const query = buildQueryParams({
      seed_paper_ids: ids.join(','),
      prompt_ids: promptIds?.length ? promptIds.join(',') : null,
      prompt_aliases: aliasesParam,
      comparison_profile_id: comparisonProfileId,
      system_keys: resolvedSystemKeys.length ? resolvedSystemKeys.join(',') : null,
      include_partial: includePartial,
      latest_only: latestOnly,
    });
    return this.request(`/api/evaluation/executions/compare${query}`);
  }

  async getMetricsExplanation() {
    return this.request('/api/evaluation/metrics-explanation');
  }

  async getEvaluationHealth() {
    return this.request('/api/evaluation/health');
  }

  // LLM Systems
  /**
   * @param {string|null} [name]
   * @param {string|null} [llmFunction] query param `function`
   * @param {string|null} [modelVersion] query param `model_version`
   * @param {string|null} [subscriptionStatus] query param `subscription_status`
   */
  async getLLMSystems(name = null, llmFunction = null, modelVersion = null, subscriptionStatus = null) {
    const query = buildQueryParams({
      name,
      function: llmFunction,
      model_version: modelVersion,
      subscription_status: subscriptionStatus,
    });
    return this.request(`/api/llm-systems${query}`);
  }

  async getLLMSystemById(llmSystemId) {
    return this.request(`/api/llm-systems/${llmSystemId}`);
  }

  // Batch Evaluation
  async evaluateBatchExecutions(seedPaperId, promptId = null, llmSystemId = null, includePartial = true) {
    const params = new URLSearchParams();
    
    if (promptId !== null && promptId !== undefined) {
      params.append('prompt_id', promptId);
    }
    if (llmSystemId !== null && llmSystemId !== undefined) {
      params.append('llm_system_id', llmSystemId);
    }
    params.append('include_partial', includePartial);
    
    const url = `/api/evaluation/batch/${seedPaperId}?${params.toString()}`;
    return this.request(url);
  }

  async compareLLMSystems(seedPaperId, promptId = null, includePartial = true) {
    const params = new URLSearchParams();
    
    if (promptId !== null && promptId !== undefined) {
      params.append('prompt_id', promptId);
    }
    params.append('include_partial', includePartial);
    
    const url = `/api/evaluation/compare-llms/${seedPaperId}?${params.toString()}`;
    return this.request(url);
  }

  // Evaluation - Relevance Only
  async evaluateRelevanceOnly(comparisonResults, includePartial = true) {
    return this.request(`/api/evaluation/relevance-only?include_partial=${includePartial}`, {
      method: 'POST',
      body: JSON.stringify(comparisonResults),
    });
  }

  // Evaluation - Validity Only
  async evaluateValidityOnly(verificationResults) {
    return this.request('/api/evaluation/validity-only', {
      method: 'POST',
      body: JSON.stringify(verificationResults),
    });
  }

  // Evaluation - Compare
  async compareEvaluations(evaluations, labels = null) {
    const requestBody = {
      evaluations: evaluations,
    };
    if (labels) {
      requestBody.labels = labels;
    }
    return this.request('/api/evaluation/compare', {
      method: 'POST',
      body: JSON.stringify(requestBody),
    });
  }

  // Comparison profiles
  async listComparisonProfiles(purpose = null) {
    const query = purpose != null && purpose !== '' ? buildQueryParams({ purpose }) : '';
    return this.request(`/api/comparison-profiles${query}`);
  }

  async getComparisonProfile(profileId) {
    return this.request(`/api/comparison-profiles/${profileId}`);
  }

  async createComparisonProfile(body) {
    return this.request('/api/comparison-profiles', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async updateComparisonProfile(profileId, body) {
    return this.request(`/api/comparison-profiles/${profileId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  async deleteComparisonProfile(profileId) {
    return this.request(`/api/comparison-profiles/${profileId}`, {
      method: 'DELETE',
    });
  }

  async replaceComparisonProfileRules(profileId, rules) {
    return this.request(`/api/comparison-profiles/${profileId}/rules`, {
      method: 'PUT',
      body: JSON.stringify(rules),
    });
  }

  async validateComparisonProfileSample(profileId, body) {
    return this.request(`/api/comparison-profiles/${profileId}/validate`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async reclassifyGt(executionId, body) {
    return this.request(
      `/api/comparison-profiles/executions/${executionId}/reclassify-gt`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      }
    );
  }

  async reclassifyVerification(executionId, body) {
    return this.request(
      `/api/comparison-profiles/executions/${executionId}/reclassify-verification`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      }
    );
  }

  _workflowStreamBaseUrl() {
    if (this.baseURL) {
      return this.baseURL.replace(/^http/i, 'ws');
    }
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}`;
  }

  // SSE: GET /api/workflow/{execution_id}/events (polls store ~1s)
  connectWorkflowEvents(executionId, onMessage, onError, token = null) {
    const url = appendWorkflowAuthQuery(
      `${this.baseURL}/api/workflow/${executionId}/events`,
      token
    );
    const eventSource = new EventSource(url);

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        onMessage(data);
      } catch (error) {
        console.error('Failed to parse SSE message:', error);
        if (onError) onError(error);
      }
    };

    eventSource.onerror = (error) => {
      console.error('SSE error:', error);
      if (onError) onError(error);
    };

    return eventSource;
  }

  /**
   * Fetch-stream SSE with Authorization Bearer (native EventSource cannot set auth headers).
   * @param {string} url
   * @param {(data: object) => void} onMessage
   * @param {(error: Error) => void} [onError]
   * @param {string|null} [token]
   * @param {string} [label] - for console errors
   * @returns {{ close: () => void }}
   */
  _connectFetchEventStream(url, onMessage, onError, token = null, label = 'SSE') {
    const controller = new AbortController();
    let closed = false;

    const close = () => {
      closed = true;
      try {
        controller.abort();
      } catch {
        /* ignore */
      }
    };

    const run = async () => {
      try {
        const headers = {
          Accept: 'text/event-stream',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        };
        const response = await fetch(url, {
          method: 'GET',
          headers,
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        if (!response.body) {
          throw new Error('SSE response has no body');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (!closed) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          const parts = buffer.split(/\r?\n\r?\n/);
          buffer = parts.pop() ?? '';

          for (const part of parts) {
            const dataLines = [];
            for (const line of part.split(/\r?\n/)) {
              if (line.startsWith('data:')) {
                dataLines.push(line.slice(5).trimStart());
              }
            }
            if (dataLines.length === 0) continue;
            const raw = dataLines.join('\n');
            if (!raw || raw === '[DONE]') continue;
            try {
              const data = JSON.parse(raw);
              onMessage(data);
            } catch (error) {
              console.error(`Failed to parse ${label} message:`, error);
              if (onError) onError(error instanceof Error ? error : new Error(String(error)));
            }
          }
        }
      } catch (error) {
        if (closed || error?.name === 'AbortError') return;
        console.error(`${label} error:`, error);
        if (onError) onError(error instanceof Error ? error : new Error(String(error)));
      }
    };

    run();
    return { close };
  }

  /**
   * Fetch-stream SSE after execution exists: GET /api/executions/{execution_id}/events
   * @param {string|number} executionId
   * @param {(data: object) => void} onMessage
   * @param {(error: Error) => void} [onError]
   * @param {string|null} [token]
   * @returns {{ close: () => void }}
   */
  connectExecutionEvents(executionId, onMessage, onError, token = null) {
    const id = executionId != null ? String(executionId).trim() : '';
    if (!/^\d+$/.test(id)) {
      throw new Error(
        `Invalid execution_id for /api/executions/{id}/events (got "${executionId}"). Use import job events until a numeric execution_id exists.`
      );
    }
    const url = `${this.baseURL}/api/executions/${id}/events`;
    return this._connectFetchEventStream(url, onMessage, onError, token, 'Execution SSE');
  }

  /**
   * Fetch-stream SSE for async import job: GET /api/executions/import/jobs/{run_id}/events
   * Prefer the 202 `events_url` when available (via connectEventsByApiPath).
   * Stream ends on terminal completed / failed. Payload may include execution_id once known.
   * @param {string|number} runId
   * @param {(data: object) => void} onMessage
   * @param {(error: Error) => void} [onError]
   * @param {string|null} [token]
   * @returns {{ close: () => void }}
   */
  connectImportJobEvents(runId, onMessage, onError, token = null) {
    if (runId == null) throw new Error('run_id is required.');
    const raw = String(runId).trim();
    const storeKey = /^import-(\d+)$/i.exec(raw);
    const id = storeKey ? storeKey[1] : raw;
    if (!id) throw new Error('run_id is required.');
    const url = `${this.baseURL}/api/executions/import/jobs/${encodeURIComponent(id)}/events`;
    return this._connectFetchEventStream(url, onMessage, onError, token, 'Import job SSE');
  }

  /**
   * SSE against a relative API path from a 202 `events_url`.
   * @param {string} apiPath
   * @param {(data: object) => void} onMessage
   * @param {(error: Error) => void} [onError]
   * @param {string|null} [token]
   * @returns {{ close: () => void }}
   */
  connectEventsByApiPath(apiPath, onMessage, onError, token = null) {
    const path = apiPath != null ? String(apiPath).trim() : '';
    if (!path) throw new Error('events_url is required.');
    let url;
    if (/^https?:\/\//i.test(path)) {
      url = path;
    } else {
      const endpoint = path.startsWith('/') ? path : `/${path}`;
      url = `${this.baseURL}${endpoint}`;
    }
    return this._connectFetchEventStream(url, onMessage, onError, token, 'Import job SSE');
  }

  // WebSocket: WS /api/workflow/{execution_id}/stream
  connectWorkflowStream(executionId, onMessage, onError, onClose, token = null) {
    const wsUrl = appendWorkflowAuthQuery(
      `${this._workflowStreamBaseUrl()}/api/workflow/${executionId}/stream`,
      token
    );
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log('WebSocket connected for execution:', executionId);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        onMessage(data);
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
        if (onError) onError(error);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      if (onError) onError(error);
    };

    ws.onclose = (event) => {
      console.log('WebSocket closed:', event.code, event.reason);
      if (onClose) onClose(event);
    };

    return ws;
  }

  // --- Reports API (/api/reports) ---

  /**
   * @param {{ seedPaperId?: number, seedPaperIds?: number[], includePartial?: boolean, aggregateGroups?: string[] }} opts
   */
  async getReportsLlmMetrics({ seedPaperId, seedPaperIds, includePartial = true, aggregateGroups } = {}) {
    const ids = seedPaperIds?.length
      ? seedPaperIds
      : seedPaperId != null
        ? [seedPaperId]
        : [];
    if (ids.length === 0) {
      throw new Error('At least one seed paper is required.');
    }
    const query = buildQueryParams({
      seed_paper_id: ids.length === 1 ? ids[0] : null,
      seed_paper_ids: ids.length > 1 ? ids.join(',') : null,
      include_partial: includePartial,
      aggregate_groups: aggregateGroups?.length ? aggregateGroups.join(',') : null,
    });
    return this.request(`/api/reports/metrics/llm-systems${query}`);
  }

  /**
   * @param {{ seedPaperId: number, includePartial?: boolean, aggregateGroups?: string[] }} opts
   */
  async getReportsPromptMetrics({ seedPaperId, includePartial = true, aggregateGroups }) {
    const query = buildQueryParams({
      seed_paper_id: seedPaperId,
      include_partial: includePartial,
      aggregate_groups: aggregateGroups?.length ? aggregateGroups.join(',') : null,
    });
    return this.request(`/api/reports/metrics/prompts${query}`);
  }

  /**
   * Execution coverage matrix: LLM systems × seed papers × prompts.
   * @param {{
   *   status?: string,
   *   seedPaperIds?: number[],
   *   aggregateGroups?: string[],
   *   signal?: AbortSignal,
   * }} [opts]
   */
  async getExecutionCoverage({ status = 'completed', seedPaperIds, aggregateGroups, signal } = {}) {
    const query = buildQueryParams({
      status,
      seed_paper_ids: seedPaperIds?.length ? seedPaperIds.join(',') : null,
      aggregate_groups: aggregateGroups?.length ? aggregateGroups.join(',') : null,
    });
    return this.request(`/api/reports/execution-coverage${query}`, { signal });
  }

  /**
   * @param {number} seedPaperId
   * @param {{
   *   status?: string,
   *   llmSystemId?: number|null,
   *   promptId?: number|null,
   *   executionId?: number|null,
   *   latestOnly?: boolean,
   *   useCache?: boolean,
   *   aggregateGroups?: string[],
   *   signal?: AbortSignal,
   * }} [filters]
   */
  async getExistenceSeedSummary(seedPaperId, filters = {}) {
    const query = buildQueryParams({
      status: filters.status ?? 'completed',
      llm_system_id: filters.llmSystemId,
      prompt_id: filters.promptId,
      execution_id: filters.executionId,
      latest_only: filters.latestOnly ?? false,
      use_cache: filters.useCache ?? true,
      aggregate_groups: filters.aggregateGroups?.length ? filters.aggregateGroups.join(',') : null,
    });
    return this.request(`/api/reports/existence/seed-papers/${seedPaperId}/summary${query}`, {
      signal: filters.signal,
    });
  }

  async getGtComparisonSeedSummary(seedPaperId, filters = {}) {
    const query = buildQueryParams({
      status: filters.status ?? 'completed',
      llm_system_id: filters.llmSystemId,
      prompt_id: filters.promptId,
      execution_id: filters.executionId,
      latest_only: filters.latestOnly ?? false,
      use_cache: filters.useCache ?? true,
      aggregate_groups: filters.aggregateGroups?.length ? filters.aggregateGroups.join(',') : null,
    });
    return this.request(`/api/reports/gt-comparison/seed-papers/${seedPaperId}/summary${query}`, {
      signal: filters.signal,
    });
  }

  async getExistenceGroups(seedPaperId, { groupBy, status = 'completed', ...rest } = {}) {
    const query = buildQueryParams({
      group_by: groupBy,
      status,
      llm_system_id: rest.llmSystemId,
      prompt_id: rest.promptId,
      execution_id: rest.executionId,
      latest_only: rest.latestOnly ?? false,
      use_cache: rest.useCache ?? true,
      aggregate_groups: rest.aggregateGroups?.length ? rest.aggregateGroups.join(',') : null,
    });
    return this.request(`/api/reports/existence/seed-papers/${seedPaperId}/groups${query}`, {
      signal: rest.signal,
    });
  }

  async getGtComparisonGroups(seedPaperId, { groupBy, status = 'completed', ...rest } = {}) {
    const query = buildQueryParams({
      group_by: groupBy,
      status,
      llm_system_id: rest.llmSystemId,
      prompt_id: rest.promptId,
      execution_id: rest.executionId,
      latest_only: rest.latestOnly ?? false,
      use_cache: rest.useCache ?? true,
      aggregate_groups: rest.aggregateGroups?.length ? rest.aggregateGroups.join(',') : null,
    });
    return this.request(`/api/reports/gt-comparison/seed-papers/${seedPaperId}/groups${query}`, {
      signal: rest.signal,
    });
  }

  async getGtComparisonByReference(seedPaperId, filters = {}) {
    const query = buildQueryParams({
      status: filters.status ?? 'completed',
      page: filters.page ?? 1,
      page_size: filters.page_size ?? filters.pageSize ?? 50,
      latest_only: filters.latestOnly ?? false,
      use_cache: filters.useCache ?? true,
      aggregate_groups: filters.aggregateGroups?.length ? filters.aggregateGroups.join(',') : null,
    });
    return this.request(`/api/reports/gt-comparison/seed-papers/${seedPaperId}/by-gt-reference${query}`, {
      signal: filters.signal,
    });
  }

  async getExistenceExecutionCitations(executionId, filters = {}) {
    const query = buildQueryParams({
      page: filters.page ?? 1,
      page_size: filters.page_size ?? filters.pageSize ?? 50,
      classification: filters.classification,
      found: filters.found,
      sort: filters.sort,
      order: filters.order,
    });
    return this.request(`/api/reports/existence/executions/${executionId}/citations${query}`, {
      signal: filters.signal,
    });
  }

  async getGtComparisonExecutionCitations(executionId, filters = {}) {
    const query = buildQueryParams({
      page: filters.page ?? 1,
      page_size: filters.page_size ?? filters.pageSize ?? 50,
      classification: filters.classification,
      gt_reference_id: filters.gt_reference_id ?? filters.gtReferenceId,
      sort: filters.sort,
      order: filters.order,
    });
    return this.request(`/api/reports/gt-comparison/executions/${executionId}/citations${query}`, {
      signal: filters.signal,
    });
  }

  async getExistenceCitationDetail(executionId, literatureId, options = {}) {
    return this.request(
      `/api/reports/existence/executions/${executionId}/citations/${literatureId}`,
      { signal: options.signal },
    );
  }

  async getGtComparisonCitationDetail(executionId, gtReferenceId, options = {}) {
    return this.request(
      `/api/reports/gt-comparison/executions/${executionId}/citations/${gtReferenceId}`,
      { signal: options.signal },
    );
  }

  async createReportExport({ reportKind, executionId }) {
    return this.request('/api/reports/exports', {
      method: 'POST',
      body: JSON.stringify({
        report_kind: reportKind,
        execution_id: executionId,
      }),
    });
  }

  async getReportExportStatus(runId) {
    return this.request(`/api/reports/exports/${runId}`);
  }

  /** Download completed export file (if backend exposes this route). */
  async downloadReportExport(runId) {
    return this.request(`/api/reports/exports/${runId}/download`, {
      responseType: 'blob',
    });
  }

  /**
   * Extract BibTeX citations from bibliography screenshot images via OCR.
   * POST /api/citation-ocr/extract — multipart field `files` (repeat for multiple; order preserved).
   * Optional: parser=example + examples (JSON string). Omit parser for built-in.
   * Always JSON: { bibtex, ocr_text }. ocr_text is one raw Tesseract string per image,
   * in reading order. parser=example also includes unmatched and unmatched_groups.
   * Requires citation_ocr access key.
   * @param {File[]} files
   * @param {{ parserMode?: string, examples?: Array, extraExamples?: Array }} [parserOptions]
   * @returns {Promise<{ bibtex: string, ocrTexts: string[], unmatchedCount: number|null, unmatchedGroups: Array<{ count: number, sample: string }> }>}
   */
  async extractCitationOcr(files, parserOptions = {}) {
    const list = Array.isArray(files) ? files : [];
    if (list.length === 0) {
      throw new Error('No files selected');
    }
    const formData = new FormData();
    for (const file of list) {
      formData.append('files', file);
    }
    appendCitationParserFields(formData, parserOptions);
    const data = await this.request('/api/citation-ocr/extract', {
      method: 'POST',
      headers: {}, // Let browser set Content-Type for FormData (multipart/form-data)
      body: formData,
      timeout: 0,
    });
    const body = data && typeof data === 'object' && !Array.isArray(data) ? data : {};
    return {
      bibtex: typeof body.bibtex === 'string' ? body.bibtex : '',
      ocrTexts: normalizeOcrText(body.ocr_text),
      unmatchedCount: parseUnmatchedCount(body.unmatched),
      unmatchedGroups: normalizeUnmatchedGroups(body.unmatched_groups),
    };
  }
}

const apiService = new ApiService();

export default apiService;
